<template>
  <VDialog
    v-model="showPopup"
    :max-width="760"
    :disabled="isBusy"
    :persistent="isBusy"
    scrollable
  >
    <VCard
      :class="['text-assgnts-popup text-xs-left', { 'grey--text': working }]"
    >
      <ModalCloseButton
        :disabled="isBusy"
        color="white"
        @click="$emit('hide')"
      />
      <VCardTitle :class="[headerColor, 'white--text']">
        <VFlex xs12 mb-2 text-xs-center
          ><h2>
            {{ modalTitle }}
          </h2>
          <div class="mode-subtitle">{{ modeSubtitle }}</div>
        </VFlex>
      </VCardTitle>
      <VCardText class="font-weight-medium text-xs-center">
        <BaseSpinner
          v-if="loadingMode"
          message="Checking how shifts will be sent..."
          text-fg-color="black"
        />
        <BaseSpinner
          v-else-if="working"
          :message="workingMessage"
          text-fg-color="black"
        />
        <BaseSpinner
          v-else-if="refreshing"
          message="Checking which shifts were already published..."
          text-fg-color="black"
        />
        <h3 v-else-if="didComplete" class="py-4">
          {{ didSendBySms ? 'Messages Sent Successfully' : 'Published' }}<br />
          <span class="font-weight-regular"
            >Statuses will update on the page shortly.</span
          >
          <div
            v-if="closeCountdown !== null"
            class="close-countdown subheading font-weight-medium grey--text text--darken-2 pt-3"
            >Done. Closing in {{ closeCountdown }}…</div
          >
        </h3>
        <template v-else>
          <VAlert
            v-if="errorMessage"
            :value="true"
            type="error"
            outline
            class="text-xs-left mb-3"
          >
            {{ errorMessage }}
          </VAlert>
          <VAlert
            v-if="murksUnavailable"
            :value="true"
            type="warning"
            outline
            class="text-xs-left mb-3"
          >
            {{ murksUnavailableMessage }}
          </VAlert>
          <h3 v-if="numLaborersToSend" class="py-4">{{ confirmHeadline }}</h3>
          <h3 v-else class="py-4">{{ nothingToSendHeadline }}</h3>
          <h4
            v-if="laborersWithSMSIssues.length && !isShowingNames"
            class="py-4"
            >There were also {{ laborersWithSMSIssues.length }} Issues
            found.</h4
          >
          <VCheckbox
            v-if="showSmsOverride"
            v-model="shouldSendBySms"
            :label="smsOverrideLabel"
            color="orange"
            hide-details
            class="d-inline-block sms-override-checkbox mb-3"
          />
          <br v-if="showSmsOverride" />
          <BaseButton
            outline
            :disabled="isTextingOneLaborer"
            @click="isShowingNames = !isShowingNames"
            >{{ isShowingNames ? 'Hide' : 'Show' }} List</BaseButton
          >
          <VLayout v-if="isShowingNames" row wrap class="my-2">
            <VFlex xs8 offset-xs2>
              <VLayout row wrap>
                <VFlex shrink>
                  <VCheckbox
                    :input-value="allSelected"
                    hide-details
                    :indeterminate="partiallySelected"
                    :disabled="!assgntsToText.length"
                    class="select-all-checkbox d-inline-block pt-0"
                    @change="toggleSelectAll"
                  />
                </VFlex>

                <VFlex
                  class="text-xs-left font-weight-bold"
                  :class="{ 'grey--text': !assgntsToText.length }"
                  grow
                >
                  {{ allSelected ? 'Deselect All' : 'Select All' }}
                </VFlex>
              </VLayout>
              <VDivider class="mb-2" />
            </VFlex>
            <VFlex
              v-for="(assignment, index) of laborersToText"
              :key="assignment.shiftId"
              xs12
              mb-1
            >
              <TextAssignment
                :assignment="assignment"
                :index="index + 1"
                :is-selected="
                  selectedAssignmentIds.includes(assignment.shiftId)
                "
                @toggle-selection="toggleSelection"
              />
            </VFlex>
            <VFlex v-if="laborersWithSMSIssues.length" xs12>
              <h3 class="text-xs-center py-4"
                >The Following Laborers have SMS issues:</h3
              >
              <div class="text-xs-center pb-2">
                <template v-if="pendingBulkAction">
                  <p class="mb-2"
                    >{{ pendingBulkAction === 'skip' ? 'Skip' : 'Clear' }} all
                    {{ laborersWithSMSIssues.length }} SMS issue
                    {{
                      laborersWithSMSIssues.length === 1 ? 'row' : 'rows'
                    }}?</p
                  >
                  <BaseButton
                    :disabled="isBusy"
                    :loading="clearing || skipping"
                    depressed
                    :color="pendingBulkAction === 'skip' ? 'orange' : 'primary'"
                    @click="confirmBulkAction"
                    >Confirm</BaseButton
                  >
                  <BaseButton
                    :disabled="isBusy"
                    outline
                    color="grey darken-1"
                    @click="pendingBulkAction = null"
                    >Cancel</BaseButton
                  >
                </template>
                <template v-else>
                  <BaseButton
                    :disabled="isBusy"
                    depressed
                    color="primary"
                    @click="pendingBulkAction = 'clear'"
                    >Clear All</BaseButton
                  >
                  <BaseButton
                    :disabled="isBusy"
                    depressed
                    color="orange"
                    @click="pendingBulkAction = 'skip'"
                    >Skip All</BaseButton
                  >
                </template>
              </div>
            </VFlex>
            <VFlex
              v-for="(assignment, index) of laborersWithSMSIssues"
              :key="assignment.shiftId"
              xs12
              mb-1
            >
              <TextAssignment
                :assignment="assignment"
                :index="index + 1"
                :status-label="`Error: ${assignment.smsStatus}`"
                skippable
                :clearing="clearing"
                :skipping="skipping"
                :selectable="false"
                :actions-disabled="isBusy"
                @clear="clearSMSStatusForAssgnt(assignment)"
                @skip="markSMSSkippedForAssgnt(assignment)"
              />
            </VFlex>
            <VFlex v-if="laborersAlreadySent.length" xs12>
              <VLayout row align-center class="px-2 pt-4 pb-3">
                <VFlex grow class="text-xs-left">
                  <h3>{{ alreadySentHeadline }}</h3>
                </VFlex>
                <VFlex shrink class="pr-3">
                  <VSelect
                    :value="sentSort.key"
                    :items="sentSortItems"
                    label="Sort by"
                    dense
                    hide-details
                    class="sent-sort-select pt-0 mt-0"
                    :style="{ width: '180px' }"
                    @change="selectSentSort"
                  />
                </VFlex>
                <VFlex shrink>
                  <VSelect
                    v-model="shiftFilter"
                    :items="shiftFilterItems"
                    label="Shifts"
                    dense
                    hide-details
                    class="shift-filter pt-0 mt-0"
                    :style="{ width: '150px' }"
                  />
                </VFlex>
              </VLayout>
              <VLayout
                row
                class="px-2 pb-1 caption font-weight-bold grey--text text--darken-1 text-xs-left"
              >
                <VFlex
                  v-for="column of sentSortColumns"
                  :key="column.key"
                  :style="sentColumnStyles[column.column]"
                >
                  <button
                    type="button"
                    :class="[
                      'sort-header font-weight-bold',
                      { 'black--text': sentSort.key === column.key },
                    ]"
                    :title="`Sort by ${column.sortLabel.toLowerCase()}`"
                    :aria-pressed="String(sentSort.key === column.key)"
                    @click="sortSentBy(column.key)"
                  >
                    {{ column.label }}
                    <VIcon
                      v-if="sentSort.key === column.key"
                      small
                      class="sort-header-icon"
                      >{{
                        sentSort.isDescending
                          ? 'arrow_downward'
                          : 'arrow_upward'
                      }}</VIcon
                    >
                  </button>
                </VFlex>
                <VFlex :style="sentColumnStyles.action" class="text-xs-center"
                  >Clear</VFlex
                >
                <VFlex
                  v-if="showTextColumn"
                  :style="sentColumnStyles.action"
                  class="text-xs-center"
                  >Text</VFlex
                >
              </VLayout>
              <VDivider class="mb-1" />
              <div
                v-if="!visibleSentRows.length"
                class="body-1 font-weight-regular grey--text text--darken-1 text-xs-center py-3"
                >{{ emptyFilterMessage }}</div
              >
            </VFlex>
            <VFlex
              v-for="(assignment, index) of visibleSentRows"
              :key="assignment.shiftId"
              xs12
            >
              <TextAssignment
                :assignment="assignment"
                :index="alreadySentRowLabels[index]"
                :index-width="alreadySentLabelWidth"
                :status-label="sentStatusLabel(assignment)"
                :status-display="sentStatusDisplay(assignment)"
                :show-text-column="showTextColumn"
                :clearable="isClearable(assignment)"
                :clearing="clearing"
                :selectable="false"
                :textable="isTextable(assignment)"
                :status-warning="statusWarning(assignment)"
                :texting="textingLaborerId === assignment.laborerId"
                :actions-disabled="isBusy"
                @clear="clearSMSStatusForAssgnt(assignment)"
                @text="textOneLaborer(assignment)"
              />
            </VFlex>
          </VLayout>
        </template>
      </VCardText>
      <VCardActions v-if="!didComplete" class="assgnt-text-modal-footer">
        <BaseButton
          :disabled="working || isTextingOneLaborer"
          depressed
          color="blue darken-1"
          @click="toggleSMSstatusVisibility"
          >{{ getIsSMSStatusVisible ? 'Hide' : 'Show' }} SMS Status</BaseButton
        >
        <VSpacer></VSpacer>
        <BaseButton
          :disabled="!canSubmit"
          depressed
          :color="submitColor"
          @click="submit"
          >{{ submitLabel }}</BaseButton
        >
        <BaseButton
          color="blue darken-1"
          :disabled="isBusy"
          outline
          @click="$emit('hide')"
          >Cancel</BaseButton
        >
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script>
import lambdaApi from '@services/lambdaApi'
import { mapGetters, mapActions } from 'vuex'
import { smsStatuses } from '@constants/knack'
import smsStatusOf from '@utils/smsStatusOf'
import pAll from 'p-all'
import _countBy from 'lodash/countBy'
import _get from 'lodash/get'
import _groupBy from 'lodash/groupBy'
import _sortBy from 'lodash/sortBy'
import {
  sentStatuses,
  sentStatusDisplays,
  NO_NOTIFICATIONS_TOOLTIP,
} from '@constants/sentStatuses'

import VDialog from '@vuetify/VDialog'
import { VCheckbox, VAlert } from 'vuetify/lib'
import { VCardText, VCardActions } from '@vuetify/VCard'
import ModalCloseButton from '@modals/ModalCloseButton'
import TextAssignment, {
  publishResults,
  statusRowColumnStyles,
  rowTextResults,
} from '@modals/TextAssignment'

export const publishModes = Object.freeze({
  MYSHIFTS: 'myshifts',
  SMS: 'sms',
})

// Why loadMode chose the mode it did, mirrored from the publishShifts GET
// `reason`. The modal only surfaces `unreachable` to the dispatcher; the other
// three are silent because texting is the correct behavior in each.
export const readReasons = Object.freeze({
  FLAG_ON: 'flag_on',
  FLAG_OFF: 'flag_off',
  NOT_DEPLOYED: 'not_deployed',
  UNREACHABLE: 'unreachable',
})

// Same window MURKs uses for its night reveal: 4:00pm through 11:00pm inclusive.
const NIGHT_SHIFT_START_MINUTE = 16 * 60
const NIGHT_SHIFT_END_MINUTE = 23 * 60

// After this many failed submissions the dispatcher may fall back to SMS by hand.
const FAILED_ATTEMPTS_BEFORE_SMS_OVERRIDE = 2

// Day / night filter over the published list.
export const shiftFilters = Object.freeze({
  ALL: 'all',
  NIGHT: 'night',
  DAY: 'day',
})

const SHIFT_FILTER_ITEMS = Object.freeze([
  { text: 'All shifts', value: shiftFilters.ALL },
  { text: 'Night only', value: shiftFilters.NIGHT },
  { text: 'Day only', value: shiftFilters.DAY },
])

// Sortable columns of the published list; sorting is in memory only.
export const sentSortKeys = Object.freeze({
  NAME: 'name',
  JOB: 'job',
  START: 'start',
  STATUS: 'status',
})

// `column` names the row column each header sits over; the widths come from
// the row component so header and rows line up.
const SENT_SORT_COLUMNS = Object.freeze([
  {
    key: sentSortKeys.NAME,
    label: 'Laborer',
    sortLabel: 'First name',
    column: 'name',
  },
  { key: sentSortKeys.JOB, label: 'Job', sortLabel: 'Job', column: 'job' },
  {
    key: sentSortKeys.START,
    label: 'Start',
    sortLabel: 'Start time',
    column: 'start',
  },
  {
    key: sentSortKeys.STATUS,
    label: 'Status',
    sortLabel: 'Response status',
    column: 'status',
  },
])

// A finished round closes the modal by itself after this many seconds.
const AUTO_CLOSE_SECONDS = 3

const SMS_OVERRIDE_LABEL =
  'MyShifts appears to be down. Text these shifts instead.'

// Shown under the laborer's name after a one-laborer text; TEXTED needs no
// note because the row's status label already says so.
const rowTextMessages = Object.freeze({
  [rowTextResults.TEXTED]: null,
  [rowTextResults.FAILED]:
    'Not sent. The text service returned an error. Try again.',
  [rowTextResults.FAILED_ACTIVE_CONVERSATION]:
    'Not sent. They have an open text conversation with us. Try again once it ends.',
  [rowTextResults.STATUS_CHANGED]:
    'Not sent. Their status changed while this window was open, so no text is needed.',
  [rowTextResults.BOARD_NOT_READ]:
    "Not sent. Couldn't re-check the board first. Try again.",
})

// Twilio Studio allows one active execution per contact per flow.
const ACTIVE_CONVERSATION_PATTERN = /already active/i

const MURKS_UNAVAILABLE_MESSAGE =
  'MyShifts is unavailable right now; assignments will be sent by text message.'

// Shown when the re-read behind the text override fails; the override is
// switched back off so a stale list is not texted.
const BOARD_RECHECK_FAILED_MESSAGE =
  "Couldn't check which shifts were already published. Tick the box again to retry."

// On-open re-read failed: the modal stays usable on a board that may predate
// a late status.
const BOARD_REFRESH_ON_OPEN_FAILED_MESSAGE =
  "Couldn't check which shifts were already published. Statuses shown may be out of date."

export default {
  name: 'TextAssgntsModal',
  components: {
    VDialog,
    VCardText,
    VCardActions,
    VCheckbox,
    VAlert,
    ModalCloseButton,
    TextAssignment,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: null,
      reason: null,
      loadingMode: false,
      working: false,
      refreshing: false,
      clearing: false,
      skipping: false,
      didComplete: false,
      didSendBySms: false,
      isShowingNames: false,
      selectedAssignmentIds: [],
      pendingBulkAction: null,
      failedAttempts: 0,
      shouldSendBySms: false,
      errorMessage: null,
      // laborer Knack id -> publishResults value, from the last submission
      laborerResults: {},
      // The one laborer being texted from their row, if any. Kept apart from
      // `working` so the list stays on screen instead of the round spinner.
      textingLaborerId: null,
      // laborer Knack id -> { result: rowTextResults value, message }
      laborerRowTexts: {},
      shiftFilter: shiftFilters.ALL,
      sentSort: { key: sentSortKeys.NAME, isDescending: false },
      // Seconds left before a finished round closes the modal; null when idle.
      closeCountdown: null,
    }
  },
  computed: {
    ...mapGetters([
      'getAsset',
      'getAssignedAssets',
      'getAssignmentsGroupedByAsset',
      'getReportById',
      'getReports',
      'getIsSMSStatusVisible',
      'getIsAssetAssigned',
      'getPendingStatusShiftIds',
    ]),
    showPopup: {
      get() {
        return this.show
      },
      set(val) {
        if (!val) {
          this.$emit('hide')
        }
      },
    }, // showPopup

    assetType() {
      return this.$route.params.assetType
    }, // assetType

    reportDate() {
      return this.$route.params.date
    }, // reportDate

    isSmsMode() {
      return this.mode === publishModes.SMS
    }, // isSmsMode

    // Whether the next submission goes out as text messages (mode or override).
    willSendBySms() {
      return this.isSmsMode || this.shouldSendBySms
    }, // willSendBySms

    modalTitle() {
      if (!this.modeKnown) {
        return 'Labor Assignments'
      }
      return this.willSendBySms ? 'Text Labor Assignments' : 'Publish Shifts'
    }, // modalTitle

    // Texting and publishing are different protocols, so the header, its
    // subtitle and the submit button change color with whichever the next
    // submission will use (the mode, or the text override on top of it). The
    // header stays neutral until the mode is read so it does not flash.
    modeKnown() {
      return this.mode !== null
    }, // modeKnown

    modeSubtitle() {
      if (!this.modeKnown) {
        return ''
      }
      return this.willSendBySms
        ? 'Sending via Twilio'
        : 'Publishing to the MyShifts app'
    }, // modeSubtitle

    headerColor() {
      if (!this.modeKnown) {
        return 'grey darken-1'
      }
      return this.willSendBySms ? 'orange accent-2' : 'teal darken-1'
    }, // headerColor

    submitColor() {
      if (!this.modeKnown) {
        return 'grey darken-1'
      }
      return this.willSendBySms ? 'blue darken-1' : 'teal darken-1'
    }, // submitColor

    submitLabel() {
      return this.willSendBySms ? 'Text Laborers' : 'Publish Shifts'
    }, // submitLabel

    // Offered only when MURKs keeps refusing. A laborer without a push
    // subscription is texted from their own row instead (textOneLaborer).
    showSmsOverride() {
      return (
        !this.isSmsMode &&
        this.failedAttempts >= FAILED_ATTEMPTS_BEFORE_SMS_OVERRIDE
      )
    }, // showSmsOverride

    smsOverrideLabel() {
      return SMS_OVERRIDE_LABEL
    }, // smsOverrideLabel

    isTextingOneLaborer() {
      return this.textingLaborerId !== null
    }, // isTextingOneLaborer

    // Only the unreachable state is worth telling the dispatcher about: the
    // flag-off and not-deployed states text silently, which is expected.
    murksUnavailable() {
      return this.reason === readReasons.UNREACHABLE
    }, // murksUnavailable

    murksUnavailableMessage() {
      return MURKS_UNAVAILABLE_MESSAGE
    }, // murksUnavailableMessage

    canSubmit() {
      let sendable = this.modeKnown || this.shouldSendBySms
      return sendable && !this.isBusy && this.numLaborersToSend > 0
    }, // canSubmit

    workingMessage() {
      return this.willSendBySms
        ? `Sending ${this.numLaborersToSend} Texts...`
        : `Publishing shifts for ${this.numLaborersToSend} ${this.laborerLabel(
            this.numLaborersToSend
          )}...`
    }, // workingMessage

    confirmHeadline() {
      let shifts = this.validatedAssignments.length
      let laborers = this.numLaborersToSend
      return this.willSendBySms
        ? `Text ${shifts} ${this.assignmentLabel(
            shifts
          )} to ${laborers} ${this.laborerLabel(laborers)}?`
        : `Publish ${shifts} ${this.shiftLabel(
            shifts
          )} to ${laborers} ${this.laborerLabel(laborers)}?`
    }, // confirmHeadline

    nothingToSendHeadline() {
      if (!this.assgntsToText.length && this.assgntsPendingStatus.length) {
        return 'Shifts were just sent. Statuses are updating.'
      }
      if (!this.assgntsToText.length) {
        return this.willSendBySms
          ? 'All Laborers with Phone Numbers have been texted.'
          : 'All shifts have been published.'
      }
      if (!this.selectedAssignmentIds.length) {
        return 'No shifts selected.'
      }
      return 'None of the selected laborers have a phone number.'
    }, // nothingToSendHeadline

    alreadySentHeadline() {
      let shifts = this.visibleSentRows.length
      return this.isSmsMode
        ? 'Already texted'
        : `${shifts} ${this.shiftLabel(shifts)} already published`
    }, // alreadySentHeadline

    shiftFilterItems() {
      return SHIFT_FILTER_ITEMS
    }, // shiftFilterItems

    emptyFilterMessage() {
      return this.shiftFilter === shiftFilters.NIGHT
        ? 'No night shifts in this list.'
        : 'No day shifts in this list.'
    }, // emptyFilterMessage

    // Each row already has a Text button's worth of texting when the modal
    // itself texts, so the column only exists while publishing to MyShifts.
    showTextColumn() {
      return !this.isSmsMode
    }, // showTextColumn

    // The "Sort by" select lists the same columns the header cells sort on.
    sentSortItems() {
      return SENT_SORT_COLUMNS.map(({ key, sortLabel }) => ({
        text: sortLabel,
        value: key,
      }))
    }, // sentSortItems

    sentSortColumns() {
      return SENT_SORT_COLUMNS
    }, // sentSortColumns

    sentColumnStyles() {
      return statusRowColumnStyles(this.showTextColumn)
    }, // sentColumnStyles

    // The published list as shown: filtered by day / night, then sorted. A
    // multi-shift laborer's rows sort on their own (a double's night and day
    // shifts part ways under Start); the row labels still tie them together.
    visibleSentRows() {
      let rows = this.laborersAlreadySent.filter(
        (row) =>
          this.shiftFilter === shiftFilters.ALL ||
          row.isNightShift === (this.shiftFilter === shiftFilters.NIGHT)
      )
      let startMinute = (row) =>
        row.startMinute === null ? Infinity : row.startMinute
      let primary = {
        [sentSortKeys.NAME]: (row) => row.name,
        [sentSortKeys.JOB]: (row) => row.jobNumber || '',
        [sentSortKeys.START]: startMinute,
        [sentSortKeys.STATUS]: (row) =>
          sentStatusDisplays[this.sentStatusOf(row)].sortRank,
      }[this.sentSort.key]
      let sorted = _sortBy(rows, [primary, (row) => row.name, startMinute])
      return this.sentSort.isDescending ? sorted.reverse() : sorted
    }, // visibleSentRows

    // One label per visibleSentRows row, in the order shown: laborers are
    // numbered once, and a multi-shift laborer's rows are lettered ("4-A",
    // "4-B") so the count reads as laborers while every shift stays visible.
    alreadySentRowLabels() {
      let rows = this.visibleSentRows
      let shiftCounts = _countBy(rows, 'laborerId')
      let laborerNumbers = {}
      let lettersUsed = {}
      return rows.map((row) => {
        if (!(row.laborerId in laborerNumbers)) {
          laborerNumbers[row.laborerId] = Object.keys(laborerNumbers).length + 1
        }
        let number = laborerNumbers[row.laborerId]
        if (shiftCounts[row.laborerId] === 1) {
          return `${number}`
        }
        let letterIndex = lettersUsed[row.laborerId] || 0
        lettersUsed[row.laborerId] = letterIndex + 1
        return `${number}-${String.fromCharCode(65 + letterIndex)}`
      })
    }, // alreadySentRowLabels

    // Just wide enough for the longest label on screen plus its full stop.
    alreadySentLabelWidth() {
      let longest = Math.max(
        0,
        ...this.alreadySentRowLabels.map((label) => label.length)
      )
      return `${longest + 1}ch`
    }, // alreadySentLabelWidth

    // Rows on the board that were sent this session and have no Knack status
    // yet. Never re-offered: the push or text is already on its way.
    assgntsPendingStatus() {
      let pendingIds = this.getPendingStatusShiftIds
      return this.getAssignedAssets(this.assetType).filter((assgnt) =>
        pendingIds.includes(assgnt.ID)
      )
    }, // assgntsPendingStatus

    assgntsPublishedWithoutSubscription() {
      return this.getAssignedAssets(this.assetType).filter(
        (assgnt) =>
          smsStatusOf(assgnt) === smsStatuses.PUBLISHED_BUT_NO_SUBSCRIPTION
      )
    }, // assgntsPublishedWithoutSubscription

    assgntsToText() {
      let pendingIds = this.getPendingStatusShiftIds
      // Published rows are never sendable here, with or without a push
      // subscription; the latter are texted one at a time from their row.
      // Declined (NonConfirmed) rows are not sendable either: re-offering
      // one starts with its Clear button in the published list below.
      let sendableStatuses = [smsStatuses.UNSENT]
      let assgnts = this.getAssignedAssets(this.assetType)
      return assgnts.filter((assgnt) => {
        let status = smsStatusOf(assgnt)
        return (
          !pendingIds.includes(assgnt.ID) &&
          (!status.length || sendableStatuses.includes(status))
        )
      })
    }, // assgntsToText

    laborersToText() {
      return _sortBy(this.convertAssignmentsToLaborers(this.assgntsToText), [
        'name',
      ])
    }, // laborersToText

    validatedAssignments() {
      // filter out assignments that are not selected
      var validatedAssignments = this.assgntsToText.filter((assgnt) =>
        this.selectedAssignmentIds.includes(assgnt.ID)
      )

      // texts need a phone number; a MyShifts push does not
      if (this.willSendBySms) {
        validatedAssignments = validatedAssignments.filter((assgnt) =>
          this.laborerIdsWithValidPhone.includes(assgnt.LABORER[0].id)
        )
      }

      return validatedAssignments
    }, // validatedAssignments

    // Checked assignments grouped by laborer Knack id: one request per key.
    checkedAssignmentsByLaborer() {
      return _groupBy(this.validatedAssignments, (assgnt) =>
        _get(assgnt, 'LABORER[0].id')
      )
    }, // checkedAssignmentsByLaborer

    numLaborersToSend() {
      return Object.keys(this.checkedAssignmentsByLaborer).length
    }, // numLaborersToSend

    assgntsWithSMSIssues() {
      let assgnts = this.getAssignedAssets(this.assetType)
      let assgntsWithSMSIssues = assgnts.filter((assgnt) =>
        [smsStatuses.SEND_FAILURE, smsStatuses.NO_REPLY].includes(
          smsStatusOf(assgnt)
        )
      )
      return assgntsWithSMSIssues
    }, // assgntsWithSMSIssues

    laborersWithSMSIssues() {
      return this.convertAssignmentsToLaborers(this.assgntsWithSMSIssues)
    }, // laborersWithSMSIssues

    // Sent or published rows: clearable so a shift can be re-offered, never skippable.
    assgntsAlreadySent() {
      let sentStatuses = [
        smsStatuses.SEND_SUCCESS,
        smsStatuses.PUBLISHED_BUT_NO_SUBSCRIPTION,
        // Responded shifts stay listed; the response settles (Confirmed)
        // or flags (NonConfirmed) the row, it does not hide it.
        smsStatuses.CONFIRMED,
        smsStatuses.NON_CONFIRMED,
      ]
      // A no-subscription row that was just texted is pending; leave it to
      // assgntsPendingStatus or laborersAlreadySent would list it twice.
      let pendingIds = this.getPendingStatusShiftIds
      let assgnts = this.getAssignedAssets(this.assetType)
      return assgnts.filter(
        (assgnt) =>
          sentStatuses.includes(smsStatusOf(assgnt)) &&
          !pendingIds.includes(assgnt.ID)
      )
    }, // assgntsAlreadySent

    // Includes rows sent this session that Knack has not reported on yet, so
    // a laborer's result stays visible after a partial failure.
    laborersAlreadySent() {
      return _sortBy(
        this.convertAssignmentsToLaborers([
          ...this.assgntsAlreadySent,
          ...this.assgntsPendingStatus,
        ]),
        ['name']
      )
    }, // laborersAlreadySent

    assignmentsByLaborer() {
      return this.getAssignmentsGroupedByAsset(
        this.assetType,
        this.assgntsToText
      )
    }, // assignmentsByLaborer

    laborerIdsWithValidPhone() {
      let laborerIdsWithValidPhone = Object.keys(
        this.assignmentsByLaborer
      ).filter((assetId) => {
        let laborer = this.getAsset(this.assetType, assetId)
        let phone = _get(laborer, 'SMS_NOTIFICATION_PHONE_NUMBER.full')
        return !!phone
      })
      return laborerIdsWithValidPhone
    }, // laborerIdsWithValidPhone

    // The board is scoped to one report date; read it off the first assignment.
    // Not assgntsToText: a one-laborer text runs when that list may be empty.
    reportDateString() {
      let firstAssgnt = this.getAssignedAssets(this.assetType)[0]
      return _get(firstAssgnt, 'REPORT_DATE.date_formatted', 'tomorrow')
    }, // reportDateString

    allSelected() {
      return (
        this.assgntsToText.length > 0 &&
        this.assgntsToText.every((assgnt) =>
          this.selectedAssignmentIds.includes(assgnt.ID)
        )
      )
    }, // allSelected

    partiallySelected() {
      return (
        this.selectedAssignmentIds.length > 0 &&
        this.selectedAssignmentIds.length < this.assgntsToText.length
      )
    }, // partiallySelected

    isBusy() {
      return (
        this.loadingMode ||
        this.working ||
        this.refreshing ||
        this.clearing ||
        this.skipping ||
        this.isTextingOneLaborer
      )
    }, // isBusy
  },
  watch: {
    // Rows that appear are checked; rows that vanish drop out; everything the
    // dispatcher already decided on keeps its checkbox as it was.
    assgntsToText: {
      immediate: true,
      handler(newVal, oldVal) {
        let previousIds = (oldVal || []).map((a) => a.ID)
        this.selectedAssignmentIds = newVal
          .filter(
            (a) =>
              !previousIds.includes(a.ID) ||
              this.selectedAssignmentIds.includes(a.ID)
          )
          .map((a) => a.ID)
      },
    },
    // Before offering texts, read the board again so a laborer whose push was
    // accepted late is not texted on top of it.
    shouldSendBySms(isOn) {
      if (isOn) {
        this.errorMessage = null
        this.refreshBoard()
      }
    },
    show(isShown) {
      if (isShown) {
        this.open()
      } else {
        this.resetSession()
      }
    },
  },
  created() {
    if (this.show) {
      this.open()
    }
  },
  beforeDestroy() {
    this.stopCloseCountdown()
  },
  methods: {
    ...mapActions([
      'toggleSMSstatusVisibility',
      'clearLaborerSMSStatus',
      'markLaborerSMSSkipped',
      'markShiftsPendingStatus',
      'refreshAssignedAssets',
    ]),
    hide() {
      this.$emit('hide')
    }, // hide
    resetSession() {
      this.pendingBulkAction = null
      this.failedAttempts = 0
      this.shouldSendBySms = false
      this.errorMessage = null
      this.reason = null
      this.laborerResults = {}
      this.textingLaborerId = null
      this.laborerRowTexts = {}
      this.didComplete = false
      this.didSendBySms = false
      this.shiftFilter = shiftFilters.ALL
      this.sentSort = { key: sentSortKeys.NAME, isDescending: false }
      this.stopCloseCountdown()
    }, // resetSession
    sortSentBy(key) {
      this.sentSort =
        this.sentSort.key === key
          ? { key, isDescending: !this.sentSort.isDescending }
          : { key, isDescending: false }
    }, // sortSentBy
    // Picking from the select always sorts ascending; the header cell flips it.
    selectSentSort(key) {
      this.sentSort = { key, isDescending: false }
    }, // selectSentSort
    // Only a round with no failures reaches didComplete, so this never runs
    // over an error banner. It cannot be cancelled: closing is what the
    // dispatcher does next anyway.
    startCloseCountdown() {
      this.stopCloseCountdown()
      this.closeCountdown = AUTO_CLOSE_SECONDS
      this.closeTimer = setInterval(this.tickCloseCountdown, 1000)
    }, // startCloseCountdown
    tickCloseCountdown() {
      // Never close over a request that is still in flight.
      if (this.isBusy) {
        return
      }
      this.closeCountdown--
      if (this.closeCountdown <= 0) {
        this.stopCloseCountdown()
        this.$emit('hide')
      }
    }, // tickCloseCountdown
    stopCloseCountdown() {
      clearInterval(this.closeTimer)
      this.closeTimer = null
      this.closeCountdown = null
    }, // stopCloseCountdown
    // The post-publish poll stops after its window; re-read so a status that
    // landed later is honored.
    open() {
      this.loadMode()
      this.refreshBoard(BOARD_REFRESH_ON_OPEN_FAILED_MESSAGE)
    }, // open
    async loadMode() {
      this.loadingMode = true
      try {
        let { data } = await lambdaApi.get('publishShifts')
        this.mode = data.mode
        this.reason = data.reason
      } catch (error) {
        // The function call itself failed (it never classifies a MURKs signal
        // as an error). Fall back to texting, the incumbent behavior, and flag
        // it as unreachable so the dispatcher sees the banner.
        console.error('publishShifts read failed, defaulting to SMS', error)
        this.mode = publishModes.SMS
        this.reason = readReasons.UNREACHABLE
      } finally {
        this.loadingMode = false
      }
    }, // loadMode
    // Resolves to whether the read succeeded. A quiet read keeps the list on
    // screen and leaves the failure to the caller (the one-laborer text shows
    // it on the row, not in the banner).
    async refreshBoard(
      failureMessage = BOARD_RECHECK_FAILED_MESSAGE,
      isQuiet = false
    ) {
      this.refreshing = !isQuiet
      try {
        await this.refreshAssignedAssets({
          assetType: this.assetType,
          date: this.reportDate,
          rows_per_page: 1000,
        })
        return true
      } catch (error) {
        // The override was ticked on the strength of this read; without it
        // the list may still show laborers whose push has since landed.
        console.warn('Could not refresh assignment statuses', error)
        if (!isQuiet) {
          this.shouldSendBySms = false
          this.errorMessage = failureMessage
        }
        return false
      } finally {
        this.refreshing = false
      }
    }, // refreshBoard
    statusWarning({ smsStatus, rowText }) {
      let wasTexted = rowText && rowText.result === rowTextResults.TEXTED
      return smsStatus === smsStatuses.PUBLISHED_BUT_NO_SUBSCRIPTION &&
        !wasTexted
        ? NO_NOTIFICATIONS_TOOLTIP
        : null
    }, // statusWarning
    isTextable(laborerRow) {
      return (
        laborerRow.smsStatus === smsStatuses.PUBLISHED_BUT_NO_SUBSCRIPTION &&
        !laborerRow.isPendingStatus
      )
    }, // isTextable
    // An accepted shift is settled by the laborer's response: no Clear, and
    // isTextable already rules out its Text button.
    isClearable(laborerRow) {
      return (
        !laborerRow.isPendingStatus &&
        laborerRow.smsStatus !== smsStatuses.CONFIRMED
      )
    }, // isClearable
    // Texts exactly one published-without-subscription laborer through the
    // function's shouldSendBySms short-circuit; MURKs is never called and the
    // modal stays in its publish mode.
    async textOneLaborer({ laborerId }) {
      this.errorMessage = null
      this.textingLaborerId = laborerId
      this.setRowText(laborerId, null)
      try {
        this.setRowText(laborerId, await this.sendOneLaborerText(laborerId))
      } finally {
        this.textingLaborerId = null
      }
    }, // textOneLaborer
    async sendOneLaborerText(laborerId) {
      if (!(await this.refreshBoard(null, true))) {
        return rowTextResults.BOARD_NOT_READ
      }
      // Re-derived after the read so a late push or a Clear is honored.
      let assgnts = this.assgntsPublishedWithoutSubscription.filter(
        (assgnt) => _get(assgnt, 'LABORER[0].id') === laborerId
      )
      if (!assgnts.length) {
        return rowTextResults.STATUS_CHANGED
      }
      let { result, errorDetail } = await this.publishForLaborer(
        laborerId,
        assgnts,
        true
      )
      if (result !== publishResults.FAILED) {
        // Statuses now trickle in from Knack; make sure the board shows them.
        this.revealSMSStatuses()
        return rowTextResults.TEXTED
      }
      return ACTIVE_CONVERSATION_PATTERN.test(errorDetail)
        ? rowTextResults.FAILED_ACTIVE_CONVERSATION
        : rowTextResults.FAILED
    }, // sendOneLaborerText
    // Turn the board's SMS status chips on (never off) after a send, so the
    // statuses updating in the background are visible without the toggle.
    revealSMSStatuses() {
      if (!this.getIsSMSStatusVisible) {
        this.toggleSMSstatusVisibility()
      }
    }, // revealSMSStatuses
    setRowText(laborerId, result) {
      let rowText = result ? { result, message: rowTextMessages[result] } : null
      this.laborerRowTexts = { ...this.laborerRowTexts, [laborerId]: rowText }
    }, // setRowText
    async submit() {
      this.working = true
      this.errorMessage = null
      let sendBySms = this.willSendBySms
      let requests = Object.entries(
        this.checkedAssignmentsByLaborer
      ).map(([laborerId, assgnts]) => () =>
        this.publishForLaborer(laborerId, assgnts, sendBySms)
      )
      try {
        let results = await pAll(requests, {
          concurrency: sendBySms ? 3 : 10,
        })
        this.recordResults(results, sendBySms)
      } finally {
        this.working = false
      }
    }, // submit
    async publishForLaborer(laborerKnackId, assgnts, shouldSendBySms) {
      try {
        let { data } = await lambdaApi.post(
          'publishShifts',
          this.buildPayload(laborerKnackId, assgnts, shouldSendBySms)
        )
        let result =
          data.mode === publishModes.SMS
            ? publishResults.TEXTED
            : publishResults.PUBLISHED
        this.markShiftsPendingStatus(assgnts.map((assgnt) => assgnt.ID))
        return { laborerKnackId, result }
      } catch (error) {
        console.error(
          `Publish failed for laborer ${laborerKnackId}:`,
          _get(error, 'response.data.error', error.message)
        )
        let detail = _get(error, 'response.data.detail', '')
        return {
          laborerKnackId,
          result: publishResults.FAILED,
          errorDetail:
            typeof detail === 'string' ? detail : JSON.stringify(detail),
        }
      }
    }, // publishForLaborer
    recordResults(results, sendBySms) {
      let laborerResults = { ...this.laborerResults }
      results.forEach(({ laborerKnackId, result }) => {
        laborerResults[laborerKnackId] = result
      })
      this.laborerResults = laborerResults

      let failedLaborerIds = results
        .filter(({ result }) => result === publishResults.FAILED)
        .map(({ laborerKnackId }) => laborerKnackId)

      if (!failedLaborerIds.length) {
        this.didSendBySms = sendBySms
        this.didComplete = true
        // Statuses now trickle in from Knack; make sure the board shows them.
        this.revealSMSStatuses()
        this.startCloseCountdown()
        return
      }

      this.failedAttempts++
      let n = failedLaborerIds.length
      let laborers = `${n} ${this.laborerLabel(n)}`
      this.errorMessage = sendBySms
        ? `Text messages failed for ${laborers}. They received nothing. They are still ticked below; press Text Laborers to retry.`
        : `MyShifts did not accept the shifts for ${laborers}. Nothing was published to them. They are still ticked below; press Publish Shifts to retry.`
      // Leave only the failed laborers checked so the retry targets them.
      this.selectedAssignmentIds = this.validatedAssignments
        .filter((assgnt) =>
          failedLaborerIds.includes(_get(assgnt, 'LABORER[0].id'))
        )
        .map((assgnt) => assgnt.ID)
    }, // recordResults
    buildPayload(laborerKnackId, assgnts, shouldSendBySms) {
      let laborer = this.getAsset(this.assetType, laborerKnackId)
      let assgntsByStartTime = _sortBy(assgnts, [
        (assgnt) => _get(assgnt, 'START_TIME.unix_timestamp', Infinity),
      ])
      let phone = _get(laborer, 'SMS_NOTIFICATION_PHONE_NUMBER.full', '')
      return {
        laborerKnackId,
        reportDate: this.reportDateString,
        shiftKnackIds: assgnts.map((assgnt) => assgnt.ID),
        shouldSendBySms,
        smsBody: this.buildSmsBody(assgntsByStartTime),
        smsTo: phone ? `+1${phone}` : '',
        laborerName: laborer.LABORER_NAME,
        startLocation: _get(
          assgntsByStartTime,
          '[0].LOCATION_START_',
          'No Location'
        ),
      }
    }, // buildPayload
    buildSmsBody(assgntsByStartTime) {
      let body = `Your assignment${
        assgntsByStartTime.length > 1 ? 's' : ''
      } for ${this.reportDateString}:\n`

      body += assgntsByStartTime
        .map((assgnt) => {
          return `${assgnt.LOCATION_IDENTIFIER} - ${assgnt.LOCATION_ADDRESS}`
        })
        .join(`\n\n`)

      let first = assgntsByStartTime[0]
      let notEnoughInfo =
        !(first.LOCATION_START_ || '').trim().length ||
        typeof first.START_TIME !== 'object'

      body += notEnoughInfo
        ? `\nPlease confirm your start location/time with your supervisor.\n`
        : `\nPlease arrive at the ${
            first.LOCATION_START_
          } at ${this.getTimeFromTimestamp(first)}.\n`
      return body
    }, // buildSmsBody

    getTimeFromTimestamp(assgnt) {
      let time = `${parseInt(assgnt.START_TIME.hours)}:${
        assgnt.START_TIME.minutes
      } ${assgnt.START_TIME.am_pm}`
      return time
    }, // getTimeFromTimestamp

    formatStartTime(assgnt) {
      return typeof assgnt.START_TIME === 'object' && assgnt.START_TIME
        ? this.getTimeFromTimestamp(assgnt)
        : null
    }, // formatStartTime

    // Minutes since midnight from Knack's { hours, minutes, am_pm } time object.
    startMinuteOfDay(assgnt) {
      let time = assgnt.START_TIME
      if (typeof time !== 'object' || !time) {
        return null
      }
      let hours = parseInt(time.hours, 10) % 12
      let minutes = parseInt(time.minutes, 10)
      if (isNaN(hours) || isNaN(minutes)) {
        return null
      }
      if (String(time.am_pm).toUpperCase() === 'PM') {
        hours += 12
      }
      return hours * 60 + minutes
    }, // startMinuteOfDay

    isNightShift(assgnt) {
      let minute = this.startMinuteOfDay(assgnt)
      return (
        minute !== null &&
        minute >= NIGHT_SHIFT_START_MINUTE &&
        minute <= NIGHT_SHIFT_END_MINUTE
      )
    }, // isNightShift

    convertAssignmentsToLaborers(assgnts) {
      let pendingIds = this.getPendingStatusShiftIds
      return _sortBy(
        assgnts.map((a) => {
          let laborerId = a.LABORER[0].id
          let laborer = this.getAsset(this.assetType, laborerId)
          let phone = _get(laborer, 'SMS_NOTIFICATION_PHONE_NUMBER.full', '')
          let numAssgnts = this.getIsAssetAssigned(this.assetType, laborer.ID)
          return {
            name: a.LABORER_NAME,
            phone,
            numAssgnts,
            shiftId: a.ID,
            laborerId,
            smsStatus: smsStatusOf(a),
            reportId: a.DAILY_REPORT[0].id,
            reportName: a.DAILY_REPORT[0].identifier,
            jobNumber: a.JOB_NUMBER,
            startTime: this.formatStartTime(a),
            startMinute: this.startMinuteOfDay(a),
            isNightShift: this.isNightShift(a),
            result: this.laborerResults[laborerId] || null,
            rowText: this.laborerRowTexts[laborerId] || null,
            isPendingStatus: pendingIds.includes(a.ID),
          }
        }),
        (l) => {
          let parts = l.name.split(' ')
          return parts[parts.length - 1].charAt(0)
        }
      )
    }, // convertAssignmentsToLaborers
    sentStatusOf({ smsStatus, isPendingStatus, result, rowText }) {
      // Twilio writes SendSuccess over the no-subscription status; for the
      // rest of this session the row still says how the laborer was reached.
      if (rowText && rowText.result === rowTextResults.TEXTED) {
        return sentStatuses.TEXTED
      }
      if (isPendingStatus) {
        return result === publishResults.TEXTED
          ? sentStatuses.TEXTED
          : sentStatuses.PUBLISHED
      }
      if (smsStatus === smsStatuses.PUBLISHED_BUT_NO_SUBSCRIPTION) {
        return sentStatuses.PUBLISHED_NO_NOTIFICATIONS
      }
      if (smsStatus === smsStatuses.CONFIRMED) {
        return sentStatuses.ACCEPTED
      }
      if (smsStatus === smsStatuses.NON_CONFIRMED) {
        return sentStatuses.DECLINED
      }
      return this.isSmsMode ? sentStatuses.TEXTED : sentStatuses.PUBLISHED
    }, // sentStatusOf
    sentStatusDisplay(laborerRow) {
      return sentStatusDisplays[this.sentStatusOf(laborerRow)]
    }, // sentStatusDisplay
    sentStatusLabel(laborerRow) {
      return this.sentStatusDisplay(laborerRow).label
    }, // sentStatusLabel
    async clearSMSStatusForAssgnt(laborer) {
      if (!laborer.shiftId) {
        console.warn('Laborer ID required to clearSMSStatus')
        return
      }
      this.clearing = true
      try {
        await this.clearLaborerSMSStatus({ shiftId: laborer.shiftId })
        this.$notify({
          type: 'success',
          title: 'SMS Status Cleared',
          text: `SMS status cleared for ${laborer.name}`,
        })
      } catch (error) {
        console.error('Failed to clear SMS status', error)
        this.$notify({
          type: 'error',
          title: 'Failed to Clear SMS Status',
          text: `Could not clear SMS status for ${laborer.name}: ${_get(
            error,
            'response.data.message',
            error.message
          )}`,
        })
      } finally {
        this.clearing = false
      }
    }, // clearSMSStatusForAssgnt
    async markSMSSkippedForAssgnt(laborer) {
      if (!laborer.shiftId) {
        console.warn('Laborer ID required to markSMSSkipped')
        return
      }
      this.skipping = true
      try {
        await this.markLaborerSMSSkipped({ shiftId: laborer.shiftId })
        this.$notify({
          type: 'success',
          title: 'SMS Skipped',
          text: `SMS marked skipped for ${laborer.name}`,
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Failed to Mark Skipped',
          text: `Could not mark SMS skipped for ${laborer.name}.`,
        })
      } finally {
        this.skipping = false
      }
    }, // markSMSSkippedForAssgnt
    confirmBulkAction() {
      if (this.pendingBulkAction === 'skip') {
        this.skipAllSMSIssues()
      } else if (this.pendingBulkAction === 'clear') {
        this.clearAllSMSIssues()
      }
    }, // confirmBulkAction
    async skipAllSMSIssues() {
      let laborers = this.laborersWithSMSIssues
      if (!laborers.length) {
        return
      }
      this.skipping = true
      try {
        let results = await this.runBulkSMSAction(laborers, (laborer) =>
          this.markLaborerSMSSkipped({ shiftId: laborer.shiftId })
        )
        this.notifyBulkResult(
          results,
          'SMS Skipped',
          (n) => `SMS marked skipped for ${n} ${this.assignmentLabel(n)}.`
        )
      } finally {
        this.skipping = false
        this.pendingBulkAction = null
      }
    }, // skipAllSMSIssues
    async clearAllSMSIssues() {
      let laborers = this.laborersWithSMSIssues
      if (!laborers.length) {
        return
      }
      this.clearing = true
      try {
        let results = await this.runBulkSMSAction(laborers, (laborer) =>
          this.clearLaborerSMSStatus({ shiftId: laborer.shiftId })
        )
        this.notifyBulkResult(
          results,
          'SMS Status Cleared',
          (n) => `SMS status cleared for ${n} ${this.assignmentLabel(n)}.`
        )
      } finally {
        this.clearing = false
        this.pendingBulkAction = null
      }
    }, // clearAllSMSIssues
    runBulkSMSAction(laborers, action) {
      // Wrap each call so one failure never aborts the rest; collect per-row outcomes.
      let requests = laborers.map((laborer) => async () => {
        try {
          await action(laborer)
          return { is_success: true, name: laborer.name }
        } catch (error) {
          return { is_success: false, name: laborer.name }
        }
      })
      return pAll(requests, { concurrency: 3 })
    }, // runBulkSMSAction
    notifyBulkResult(results, successTitle, buildSuccessText) {
      let failures = results.filter((result) => !result.is_success)
      let successCount = results.length - failures.length
      if (successCount) {
        this.$notify({
          type: 'success',
          title: successTitle,
          text: buildSuccessText(successCount),
        })
      }
      if (failures.length) {
        this.$notify({
          type: 'error',
          title: 'Some Updates Failed',
          text: `Could not update ${failures.length} ${this.assignmentLabel(
            failures.length
          )}: ${failures.map((failure) => failure.name).join(', ')}.`,
        })
      }
    }, // notifyBulkResult
    assignmentLabel(count) {
      return count === 1 ? 'assignment' : 'assignments'
    }, // assignmentLabel
    shiftLabel(count) {
      return count === 1 ? 'shift' : 'shifts'
    }, // shiftLabel
    laborerLabel(count) {
      return count === 1 ? 'laborer' : 'laborers'
    }, // laborerLabel
    // The banner describes the last round; once the dispatcher changes what
    // the next round will do, it no longer applies.
    toggleSelection(shiftId) {
      this.errorMessage = null
      const index = this.selectedAssignmentIds.indexOf(shiftId)
      if (index === -1) {
        this.selectedAssignmentIds.push(shiftId)
      } else {
        this.selectedAssignmentIds.splice(index, 1)
      }
    },
    toggleSelectAll() {
      this.errorMessage = null
      if (this.allSelected) {
        this.selectedAssignmentIds = []
      } else {
        this.selectedAssignmentIds = this.assgntsToText.map((a) => a.ID)
      }
    },
  }, // methods
}
</script>

<style lang="scss">
.text-assgnts-popup {
  .v-card__title {
    .title,
    .subtitle {
      font-family: 'Avenir', sans-serif !important;
      font-weight: 500;
      color: black;
    }
    .subtitle {
      margin-top: 0.3em;
    }
    .mode-subtitle {
      min-height: 1.4em;
      margin-top: 0.15em;
      font-size: 0.95em;
      font-weight: 400;
      opacity: 0.9;
    }
  }
  .no-phone-icon {
    color: $red;
  }
  .num-assignments {
    flex: 0 0 auto;
    margin-left: 4px;
    font-size: 0.8em;
    line-height: 15px;
    color: white;
    background-color: $orange;
    border-radius: 100% !important;
  }
  .sort-header {
    font: inherit;
    color: inherit;
    cursor: pointer;
    background: none;
    border: 0;
    outline: none;
    &:hover,
    &:focus {
      color: black;
    }
    .sort-header-icon {
      font-size: 13px;
      vertical-align: -2px;
    }
  }
  .sms-override-checkbox {
    .v-label {
      font-weight: 500;
      color: $orange;
    }
  }
}
</style>
